import { sanitize } from 'dompurify';
import qs from 'qs';
import { InitialPagination_S } from '../features/pagination/types/initialPagination.schema';
import { InitialPagination } from '../features/pagination/types/initialPagination.type';
import { State } from '../state/types/state';
import { Snapshot } from './Snapshot';
import { SnapshotSets, SnapshotSets_S } from './types/snapshot-sets';
import { URLPagination } from './types/url-storage';

/**
 * Handles persistence of search query and search state in the URL.
 * This class is only meant to be used as a singleton (prevents url params getting out of sync).
 */
export class URLStorage {
    private static instance: URLStorage;

    public static getInstance(): URLStorage {
        if (!URLStorage.instance) {
            URLStorage.instance = new URLStorage();
        }

        return URLStorage.instance;
    }

    private snapshot: Snapshot;
    private searchParams: URLSearchParams;
    private pagination: URLPagination;

    readonly #SR_QUERY_URL_PARAM = 'srquery';
    readonly #SR_CONTROLS_STATE_URL_PARAM = 'srstate';
    readonly #SR_PAGINATION_URL_PARAM = 'srpagination';
    readonly #URL_MAX_LENGTH = 2048; // setting longer than this will currently not trigger exception as safari, chrome, firefox all support longer URLs

    private constructor() {
        this.searchParams = new URLSearchParams(window.location.search);
        this.snapshot = new Snapshot();
        this.pagination = {};
    }

    public updateSearchParams(): URLSearchParams {
        this.searchParams = new URLSearchParams(window.location.search);
        this.updateURL();
        return this.searchParams;
    }

    public getSearchParams(): URLSearchParams {
        return this.searchParams;
    }

    public clearSearchParams(): URLSearchParams {
        this.searchParams.delete(this.#SR_QUERY_URL_PARAM);
        this.searchParams.delete(this.#SR_CONTROLS_STATE_URL_PARAM);
        this.searchParams.delete(this.#SR_PAGINATION_URL_PARAM);
        this.updateURL();

        return this.searchParams;
    }

    public setSearchQuery(searchQuery: string): URLSearchParams {
        // if there is no search query, remove the param from the URL
        if (searchQuery === '') {
            this.searchParams.delete(this.#SR_QUERY_URL_PARAM);
            this.updateURL();
            return this.searchParams;
        }

        // otherwise, write the search query to the URL
        this.searchParams.set(this.#SR_QUERY_URL_PARAM, searchQuery);
        this.updateURL();
        return this.searchParams;
    }

    public setControlsState(controls: State['controls']): URLSearchParams {
        const controlsSnapshot = this.snapshot.createControlsSnapshotFromState(controls);

        // if there are no controls saved, remove the param from the URL
        if (controlsSnapshot.length === 0) {
            this.searchParams.delete(this.#SR_CONTROLS_STATE_URL_PARAM);
            this.updateURL();
            return this.searchParams;
        }

        const serializedSnapshot = this.URLSerialize(controlsSnapshot);

        if (serializedSnapshot.length > this.#URL_MAX_LENGTH) {
            console.warn('recommended URL length exceeded, search state might not be fully persisted in the URL');
        }

        // otherwise, write the serialized state to the URL
        this.searchParams.set(this.#SR_CONTROLS_STATE_URL_PARAM, serializedSnapshot);
        this.updateURL();
        return this.searchParams;
    }

    public updatePagination(querySetIdentifier: string, size: number): URLSearchParams {
        this.pagination[querySetIdentifier] = { size };

        this.searchParams.set(this.#SR_PAGINATION_URL_PARAM, this.URLSerialize(this.pagination));
        this.updateURL();
        return this.searchParams;
    }

    public getPagination(): InitialPagination | null {
        const paginationString = this.searchParams.get(this.#SR_PAGINATION_URL_PARAM);
        if (!paginationString) {
            return null;
        }
        return this.URLParsePagination(paginationString);
    }

    public getSearchQuery(): string | null {
        const query = this.searchParams.get(this.#SR_QUERY_URL_PARAM);
        return query ? sanitize(query, { ALLOWED_TAGS: ['#text'] }) : null;
    }

    public getControlsState(): SnapshotSets | null {
        const stringSnapshot = this.searchParams.get(this.#SR_CONTROLS_STATE_URL_PARAM);
        if (!stringSnapshot) {
            return null;
        }
        return this.URLParseSnapshot(stringSnapshot);
    }

    private URLSerialize(snapshot: SnapshotSets | URLPagination): string {
        // set encode to true to enable special characters
        const serialized = qs.stringify(snapshot, { encode: false, delimiter: '&' });
        return serialized;
    }

    private URLParse(URLString: string) {
        return qs.parse(URLString, {
            ignoreQueryPrefix: true,
            // needed because qs parses all data to string by default
            decoder(str, decoder, charset) {
                const strWithoutPlus = str.replace(/\+/g, ' ');
                if (charset === 'iso-8859-1') {
                    return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
                }

                if (/^(\d+|\d*\.\d+)$/.test(str)) {
                    return parseFloat(str);
                }

                const keywords: { [key: string]: boolean | null | undefined } = {
                    true: true,
                    false: false,
                    null: null,
                    undefined: undefined,
                };

                if (str in keywords) {
                    return keywords[str];
                }

                // utf-8
                try {
                    return decodeURIComponent(strWithoutPlus);
                } catch (e) {
                    return strWithoutPlus;
                }
            },
        });
    }

    private URLParseSnapshot(snapshotString: string): SnapshotSets {
        const parsedArray = Object.values(this.URLParse(snapshotString));
        return SnapshotSets_S.parse(parsedArray);
    }

    private URLParsePagination(paginationString: string): InitialPagination {
        try {
            return InitialPagination_S.parse(this.URLParse(paginationString));
        } catch (e) {
            console.error(`URL paggination parsing error for string ${paginationString}`);
        }
        return {};
    }

    private updateURL(): void {
        window.history.replaceState(null, '', `${window.location.pathname}?${this.searchParams}`);
    }
}
